import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useWrapRef} from '../common/useWrapRef.hook';
import {LazyLoadContextProps} from './LazyLoadContext.interface';
import {
  AddLazyLoadHandler,
  LazyLoadParams,
  RemoveLazyLoadHandler,
  useLazyLoadProps,
} from './useLazyLoad.hook.interface';

/**
 * lazy load 할 수 있게 도와주는 훅입니다.
 * IntersectionObserver 객체 하나만 생성해서 여러 요소를 동시에 옵저빙 할 수 있도록 합니다.
 *
 * 사용 방법은 아래와 같습니다.
 *   - LazyLoadWrapper 컴포넌트 또는 useLazyLoad 훅을 부모 요소에서 사용합니다.
 *   - 자식 컴포넌트에 useLazyLoadIntersection 훅을 사용합니다.
 *   - 자식 컴포넌트가 마운트 되면 addLazyLoad 함수를 이용해 옵저버에 자동으로 등록됩니다.
 *   - 해당 요소가 옵저버에 의해 뷰포트에 보여지면 removeLazyLoad를 실행해 옵저버에서 삭제됩니다.
 *   - useLazyLoadIntersection 이 반환하는 값을 이용해 lazy load를 구현합니다.
 *   ex)
 *     {isIntersection && <Image />}
 *     {!isIntersection && <LoadingImage />}
 */
export function useLazyLoad(params: useLazyLoadProps = {}) {
  const {disabled = false, options} = params;

  const ref = useRef<IntersectionObserver>();
  const optionRef = useWrapRef(options);
  const listRef = useRef<LazyLoadParams[]>([]);
  const lazyCallListRef = useRef<LazyLoadParams[]>([]);

  const addLazyLoad = useCallback<AddLazyLoadHandler>(
    params => {
      if (disabled) return;

      const observer = ref.current;
      // 옵저버가 생성되기 전, 자식 컴포넌트가 먼저 렌더링 된다면,
      // 리스트에 쌓아두고 옵저버가 생성되면 쌓인 리스트를 옵저버에 등록한다.
      if (!observer) {
        lazyCallListRef.current.push(params);
        return;
      }

      observer.observe(params.el);
      listRef.current.push(params);
    },
    [disabled]
  );

  const removeLazyLoad = useCallback<RemoveLazyLoadHandler>(el => {
    const observer = ref.current;
    const list = listRef.current;
    if (!observer || list.length === 0) return;

    const index = list.findIndex(item => item.el === el);
    if (index !== -1) {
      list.splice(index, 1);
      observer.unobserve(el);
    }
  }, []);

  const memoizedContext = useMemo<LazyLoadContextProps>(
    () => ({disabled, addLazyLoad, removeLazyLoad}),
    [disabled, addLazyLoad, removeLazyLoad]
  );

  useEffect(() => {
    if (disabled) return;

    ref.current = new IntersectionObserver(
      entries => {
        const list = listRef.current;
        entries.forEach(entry => {
          const target = entry.target as HTMLElement;
          const item = list.find(item => item.el === target);
          item?.onIntersection(entry.isIntersecting, target);
        });
      },
      {...optionRef.current}
    );

    lazyCallListRef.current.forEach(addLazyLoad);
    lazyCallListRef.current = [];

    return () => ref.current?.disconnect();
  }, [disabled, optionRef, addLazyLoad]);

  return {
    memoizedContext,
    addLazyLoad,
    removeLazyLoad,
  };
}
